import { AnimalFigure } from "@/components/camp/AnimalFigure";
import type { AnimalId } from "@/components/camp/campData";
import { cn } from "@/engine/support/classNames";

/**
 * The frame every section of the landing shares: a small line over a heading,
 * and an optional sentence under it. One frame, so the page reads as one page.
 */
export function LandingSection({
  id,
  eyebrow,
  title,
  lead,
  centred = false,
  className,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  lead?: string;
  centred?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      data-testid={`landing-${id}`}
      className={cn("mx-auto w-full max-w-6xl px-4 py-16 sm:py-24", className)}
    >
      <div className={cn("mb-10 max-w-3xl", centred && "mx-auto text-center")}>
        <span className="mb-3 inline-block text-xs font-semibold tracking-[0.14em] text-primary uppercase">
          {eyebrow}
        </span>
        <h2 className="text-balance text-xl font-bold tracking-tight">
          {title}
        </h2>
        {lead ? (
          <p className="mt-4 text-pretty text-lg text-muted-foreground">
            {lead}
          </p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export const LANDING_CARD =
  "rounded-3xl border border-border bg-card p-6 text-left";

/** A larger animal peering in from a card's corner, for the cards that tell a story. */
export function CornerAnimal({ animal }: { animal: AnimalId }) {
  return (
    <AnimalFigure
      aria-hidden="true"
      animal={animal}
      className="pointer-events-none absolute -top-2 -right-3 !h-28 !w-20 rotate-6 opacity-30"
    />
  );
}
